/**
 * Browser-side state used by the optional desktop-app handoff.
 *
 * Browsers cannot query whether a custom protocol handler is installed. We
 * therefore only attempt a handoff after the user has either enabled the
 * desktop preference or successfully opened the app through the legacy
 * prompt.
 */
export const PREFER_DESKTOP_APP_KEY = 'scrambl_prefer_desktop_app'
export const LEGACY_DESKTOP_APP_PREFERENCE_KEY =
  'scrambl:desktop-app-preference'
export const DESKTOP_HANDOFF_HANDLED_KEY = 'scrambl:desktop-handoff-handled'

const LEGACY_DESKTOP_HANDOFF_SESSION_KEYS = [
  'scrambl_deep_link_checked',
  'scrambl:desktop-prompt-handled',
] as const

const EXCLUDED_PATH_PREFIXES = [
  '/account',
  '/auth/',
  '/beta',
  '/client-portal',
  '/delete-account',
  '/embed/',
  '/external-org-invite',
  '/f/',
  '/help',
  '/invite',
  '/login',
  '/open',
  '/organization-setup',
  '/organizations',
  '/register',
  '/unauthorized',
  '/user-setup',
  '/whats-new',
] as const

function normalizePathname(pathname: string): string {
  const pathWithoutQuery = pathname.split(/[?#]/, 1)[0] ?? '/'
  if (pathWithoutQuery === '/') return '/'
  return `/${pathWithoutQuery.replace(/^\/+|\/+$/g, '')}`
}

function isExcludedPath(normalizedPath: string): boolean {
  return EXCLUDED_PATH_PREFIXES.some(
    prefix =>
      normalizedPath === prefix.replace(/\/$/, '') ||
      normalizedPath.startsWith(prefix.endsWith('/') ? prefix : `${prefix}/`),
  )
}

/**
 * Whether the browser has evidence that the desktop app is installed.
 *
 * The current preference is set explicitly in App Settings. The legacy value
 * is only set after a successful desktop launch, so it is also safe evidence
 * for existing users.
 */
export function isDesktopAppKnownInstalled(
  storage: Pick<Storage, 'getItem'> = localStorage,
): boolean {
  const currentPreference = storage.getItem(PREFER_DESKTOP_APP_KEY)
  if (currentPreference !== null) return currentPreference === 'true'

  return storage.getItem(LEGACY_DESKTOP_APP_PREFERENCE_KEY) === 'always'
}

/**
 * Desktop handoff is limited to direct openings of concrete organization
 * pages. Today and a bare organization URL are intentionally excluded because
 * they are the normal landing routes and should never interrupt org entry.
 */
export function isDesktopHandoffPage(pathname: string): boolean {
  const normalizedPath = normalizePathname(pathname)

  if (isExcludedPath(normalizedPath)) return false

  const segments = normalizedPath.split('/').filter(Boolean)

  // A bare /:orgSlug redirects to /:orgSlug/today.
  if (segments.length < 2) return false

  // Match both /today and /:orgSlug/today defensively.
  if (segments.at(-1) === 'today') return false

  return true
}

/** Today and a bare organization URL make the one-time decision silently. */
export function isDesktopHandoffLandingPage(pathname: string): boolean {
  const normalizedPath = normalizePathname(pathname)
  if (isExcludedPath(normalizedPath)) return false

  const segments = normalizedPath.split('/').filter(Boolean)
  return segments.length === 1 || segments.at(-1) === 'today'
}

export function hasHandledDesktopHandoff(
  storage: Pick<Storage, 'getItem'> = localStorage,
  legacyStorage: Pick<Storage, 'getItem'> = sessionStorage,
): boolean {
  return (
    storage.getItem(DESKTOP_HANDOFF_HANDLED_KEY) === 'true' ||
    LEGACY_DESKTOP_HANDOFF_SESSION_KEYS.some(
      key => legacyStorage.getItem(key) === 'true',
    )
  )
}

export function markDesktopHandoffHandled(
  storage: Pick<Storage, 'setItem'> = localStorage,
): void {
  storage.setItem(DESKTOP_HANDOFF_HANDLED_KEY, 'true')
}
